import { useRef, useState } from 'react'
import { VpButton } from '@vtmn-play/react'
import { useTranslation } from 'react-i18next'
import { LoaderFunctionArgs, useNavigate } from 'react-router-dom'
import { userPreferencesPort } from '@/infra/config/beans'
import { useUserPreferences } from '@/src/hooks/use-user-preference-port/useUserPreferencePort'
import { usePageHeaders } from '@/src/hooks/use-page-headers/usePageHeaders'
import SearchComponent from '@/src/components/search-component/SearchComponent'
import { SearchItem } from '@/src/components/search-item/SearchItem'
import { VpDivider } from '@vtmn-play/react/VpDivider'
import BackToStoreHeader from '@/src/components/account/team-preference/back-to-store-header/BackToStoreHeader'
import { runUserPreferencesGuard } from '@/src/router/guards/runUserPreferenceGuard'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getStores } from '@/infra/api/generated/stores/stores'
import { TeamDto } from '@/infra/api/generated/model'

const { getStoreTeams } = getStores()
import { trackEvent } from '@/src/analytics/amplitude'

export const Loader = ({ request }: LoaderFunctionArgs) => {
  const currentPath = new URL(request.url).pathname
  return runUserPreferencesGuard(userPreferencesPort, currentPath)
}

const SelectTeamPage = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [query, setQuery] = useState('')
  const { t } = useTranslation('common')
  usePageHeaders(t('pages.select-team.title'))
  const { store, team } = useUserPreferences(userPreferencesPort)
  const [selectedTeam, setSelectedTeam] = useState<TeamDto | undefined>(team)
  const initialTeamRef = useRef(team)
  const { data, error } = useQuery({
    queryKey: ['teams', store?.id],
    queryFn: () => getStoreTeams(store?.id!),
    enabled: Boolean(store?.id),
  })
  const hasQuery = query.trim().length > 0
  const teams = data ?? []
  const filtered = teams.filter(
    (team) =>
      team.id.toString().includes(query) || team.name.toLowerCase().includes(query.toLowerCase())
  )
  const handleSelect = async (team: TeamDto) => {
    setSelectedTeam(team)
    await queryClient.invalidateQueries({
      predicate: (query) => ['diagnosis', 'actions'].includes(query.queryKey[0] as string),
    })
    userPreferencesPort.upsertTeam(team)
  }

  const handleContinueButtonClick = () => {
    if (Boolean(initialTeamRef.current) && selectedTeam?.id !== initialTeamRef.current?.id) {
      trackEvent('TEAM_CHANGE')
    }
    void navigate('/', { viewTransition: true })
  }

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="px-4 pb-6">
        <BackToStoreHeader store={store!} />
      </div>
      <p className="text-l text-start uppercase font-semibold px-5">
        {t('pages.select-team.which-team')}
      </p>
      <SearchComponent
        query={query}
        setQuery={setQuery}
        searchLabel="pages.select-team.search"
        error={error}
        startSearchLabel={t('components.search.start-label', {
          labelContext: t('global.team', { count: 2 }),
        })}
      >
        {hasQuery &&
          filtered.map((team) => (
            <div key={team.id}>
              <SearchItem
                onClick={() => handleSelect(team)}
                selected={selectedTeam?.id === team.id}
                label={`${team.name}`}
              />
              <VpDivider />
            </div>
          ))}
      </SearchComponent>

      <div className="sticky bottom-0 px-5 pb-3">
        <VpButton
          className="w-full"
          disabled={!team?.id}
          shape="squared"
          data-testid="continue-button"
          onClick={handleContinueButtonClick}
        >
          {t('global.continue')}
        </VpButton>
      </div>
    </div>
  )
}
export default SelectTeamPage
